import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Backdrop, CircularProgress, Box } from "@mui/material";
import { ResourceLoading } from "../../utils/ResourceLoading";
import ItemSelectorV2 from "../../utils/Selector/ItemSelectorV2";
import ModernCalculationSelector from "../../utils/Selector/ModernCalculationSelector";
import OfferCalculationItems from "../offers/calculations/calculation-items";
import { OfferCalculationUserInsertView } from "../offers/calculations/module-builder";
import { Formik } from "formik";
import { toast } from "react-hot-toast";
import { TrashIcon, CalculatorIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import ProConfirmation from "../../utils/ProConfirmation";
import { QuillEditor } from "../../utils/quill-editor";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { fetchOfferCalculation } from "../../../api/offer-calculation";
import { useDispatch } from "react-redux";
import { updateOfferCalculationAsync, createOfferCalculationAsync } from "../../../slices/offer-calculation";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

const isModuleCalculation = (calculation) =>
  Boolean(
    calculation?.module_enabled ||
    calculation?.module_graph?.nodes?.length ||
    calculation?.module_result
  );

export default function OfferItems(props) {
  const { t } = useTranslation();
  const { setFieldValue, values, isLoading, name, drag } = props;
  const dispatch = useDispatch();

  const rows = values[name];
  
  // Get offer_id from values if available (for offer-specific calculations)
  const offerId = values?.id || values?.offer_id;

  var formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [calculationSelectorOpen, setCalculationSelectorOpen] = useState(false);
  const [pendingCalculationForInsert, setPendingCalculationForInsert] = useState(null);
  const [editingCalculation, setEditingCalculation] = useState(null);
  const [selectedItemForCalculation, setSelectedItemForCalculation] = useState(null);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [deletingItems, setDeletingItems] = useState(new Set());
  const [savingItems] = useState(new Set());
  // Stack for nested calculation dialogs
  const [calculationDialogStack, setCalculationDialogStack] = useState([]);
  // Dialog for choosing whether to save template or just offer version
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [pendingCalculationSave, setPendingCalculationSave] = useState(null);
  const orderItems = (rows) => {
    var array = [];

    if (!rows) return;

    rows.map((row, i) => {
      const rowParams = {
        ...row,
        row_order: i,
        draggableId: "id" + row.id,
      };
      array.push(rowParams);
    });
    return array;
  };

  // Map items from backend to include both type and item_type for compatibility
  const mapItemsFromBackend = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.map(item => ({
      ...item,
      type: item.item_type || item.type, // Use item_type if available, fallback to type
      item_type: item.item_type || item.type, // Ensure item_type is always set
    }));
  };

  // Use ref to track if update is from internal change (to avoid circular updates)
  const isInternalUpdateRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const skipNextUpdateRef = useRef(false);

  // Memoize mapped rows to avoid unnecessary recalculations
  const mappedRows = useMemo(() => {
    if (!rows || !Array.isArray(rows)) return [];
    return mapItemsFromBackend(rows);
  }, [rows]);

  // Memoize ordered items to avoid unnecessary recalculations
  const initialItems = useMemo(() => {
    return orderItems(mappedRows);
  }, [mappedRows]);

  const [items, setItems] = useState(initialItems);

  // Update items when rows prop changes (e.g., after loading from backend)
  // Only update if rows actually changed and it's not an internal update
  useEffect(() => {
    // Skip if this is an internal update or if we're skipping this update
    if (isInternalUpdateRef.current || skipNextUpdateRef.current) {
      isInternalUpdateRef.current = false;
      skipNextUpdateRef.current = false;
      return;
    }

    // Only update from parent if rows actually changed
    if (rows && Array.isArray(rows)) {
      const newMappedRows = mapItemsFromBackend(rows);
      const newOrderedItems = orderItems(newMappedRows);
      
      // Simple length and reference check to avoid unnecessary updates
      if (newOrderedItems.length !== items.length || isInitialMountRef.current) {
        setItems(newOrderedItems);
        isInitialMountRef.current = false;
      }
    }
  }, [rows]); // Removed items from dependencies to avoid circular updates

  // Update parent form when items change (but only if change is internal)
  useEffect(() => {
    // Skip initial mount
    if (isInitialMountRef.current) {
      return;
    }

    // Only update parent if this was an internal change
    if (isInternalUpdateRef.current) {
      const mappedItems = items.map(item => ({
        ...item,
        item_type: item.item_type || item.type, // Ensure item_type is set
      }));
      
      // Set flag to skip the next parent update to avoid circular updates
      skipNextUpdateRef.current = true;
      setFieldValue(name, mappedItems);
      isInternalUpdateRef.current = false;
    }
  }, [items, name, setFieldValue]);

  const addItemRow = (type) => {
    const newRow = {
      title: "",
      description: "",
      purchase_price: 0,
      quantity: 1, // Automatisk 1 i mengde ved opprettelse
      type: type,
      item_type: type, // Also set item_type for backend
      row_order: items.length,
      // Generating a fake ID for draggable, so that we are able to move the row before saving.
      draggableId: "id" + items.length + 1,
      calculation: null, // For calculation type rows
      calculation_id: null,
      group_row: type === "group", // Set group_row flag for group type
    };
    isInternalUpdateRef.current = true;
    const updatedItems = [...items, newRow];
    setItems(updatedItems);
    // Update Formik immediately so the new row is available
    if (setFieldValue) {
      const mappedItems = updatedItems.map(item => ({
        ...item,
        item_type: item.item_type || item.type,
      }));
      setFieldValue(name, mappedItems);
    }
  };

  // Helper function to calculate total cost from calculation items
  const calculateCalculationCost = (calculation) => {
    // First try to use purchase_amount if available (most accurate)
    if (calculation && calculation.purchase_amount !== undefined && calculation.purchase_amount !== null) {
      return parseFloat(calculation.purchase_amount || 0);
    }
    
    // Fallback: calculate from items
    let totalCost = 0;
    if (calculation && calculation.items && Array.isArray(calculation.items)) {
      calculation.items.forEach((item) => {
        // Try to use item.purchase_amount if available
        if (item.purchase_amount !== undefined && item.purchase_amount !== null) {
          totalCost += parseFloat(item.purchase_amount || 0);
        } else {
          // Fallback to purchase_price * quantity
          const costPrice = parseFloat(item.purchase_price || 0);
          const quantity = parseFloat(item.quantity || 1);
          totalCost += costPrice * quantity;
        }
        
        // Also check nested calculations recursively
        if (item.nested_calculation) {
          totalCost += calculateCalculationCost(item.nested_calculation);
        }
      });
    }
    return totalCost;
  };

  // Helper function to calculate total hours from calculation items
  const calculateCalculationTotalHours = (calculation) => {
    let totalHours = 0;
    if (calculation && calculation.items && Array.isArray(calculation.items)) {
      calculation.items.forEach((item) => {
        // Get hours from custom_fields or hours field
        const hours = parseFloat(item.custom_fields?.hours || item.hours || 0);
        const quantity = parseFloat(item.quantity || 1);
        totalHours += hours * quantity;
        
        // Also check nested calculations recursively
        if (item.nested_calculation) {
          totalHours += calculateCalculationTotalHours(item.nested_calculation);
        }
      });
    }
    return totalHours;
  };

  // Helper function to calculate total amount from calculation items
  const calculateCalculationTotalAmount = (calculation) => {
    let total = 0;
    if (calculation && calculation.items && Array.isArray(calculation.items)) {
      calculation.items.forEach((item) => {
        const quantity = parseFloat(item.quantity || 1);
        const salesPrice = parseFloat(item.sales_price || 0);
        const discount = parseFloat(item.discount || 0);
        const surcharge = parseFloat(item.surcharge || 0);
        
        let itemTotal = salesPrice * quantity;
        if (surcharge > 0) itemTotal += (itemTotal * surcharge) / 100;
        if (discount > 0) itemTotal -= (itemTotal * discount) / 100;
        
        total += itemTotal;
        
        // Add hours calculation if exists
        const hours = parseFloat(item.hours || 0);
        const hourRate = parseFloat(item.hour_rate || 0);
        if (hours > 0 && hourRate > 0) {
          total += hours * hourRate * quantity;
        }
        
        // Also check nested calculations recursively
        if (item.nested_calculation) {
          total += calculateCalculationTotalAmount(item.nested_calculation) * quantity;
        }
      });
    }
    return total;
  };

  const handleAddCalculationRow = () => {
    setCalculationSelectorOpen(true);
  };

  const insertCalculationInOffer = (calcData) => {
    if (!calcData) return;

    const totalCost = calculateCalculationCost(calcData);
    const totalSales = parseFloat(
      calcData.module_result?.total ||
      calcData.total_amount ||
      calculateCalculationTotalAmount(calcData) ||
      0
    );

    const calculationForRow = {
      ...calcData,
      total_amount: totalSales,
    };

    if (selectedItemForCalculation) {
      let prevItems = [...items];
      const itemIndex = prevItems.findIndex(
        (item) => item.row_order === selectedItemForCalculation.row_order
      );
      if (itemIndex !== -1) {
        prevItems[itemIndex] = {
          ...prevItems[itemIndex],
          title: calcData.title || "",
          description: calcData.description || "",
          sales_price: totalSales,
          purchase_price: totalCost,
          type: "calculation",
          item_type: "calculation",
          calculation: calculationForRow,
          calculation_id: calcData.id,
          original_calculation_id: calcData.original_calculation_id || calcData.id,
          is_copy: true,
        };
        isInternalUpdateRef.current = true;
        setItems(prevItems);
        setFieldValue(name, prevItems.map((item) => ({ ...item, item_type: item.item_type || item.type })));
      }
    } else {
      const newRow = {
        title: calcData.title || "",
        description: calcData.description || "",
        purchase_price: totalCost,
        sales_price: totalSales,
        quantity: 1,
        type: "calculation",
        item_type: "calculation",
        row_order: items.length,
        draggableId: "id" + (items.length + 1),
        calculation: calculationForRow,
        calculation_id: calcData.id,
        original_calculation_id: calcData.original_calculation_id || calcData.id,
        is_copy: true,
      };
      const nextItems = [...items, newRow];
      isInternalUpdateRef.current = true;
      setItems(nextItems);
      setFieldValue(name, nextItems.map((item) => ({ ...item, item_type: item.item_type || item.type })));
    }

    setPendingCalculationForInsert(null);
    setCalculationSelectorOpen(false);
    setSelectedItemForCalculation(null);
    toast.success("Kalkyle lagt til i tilbudet");
  };

  const handleAdjustCalculationFields = (item) => {
    if (!item?.calculation) return;

    setSelectedItemForCalculation(item);
    setPendingCalculationForInsert({
      ...item.calculation,
      total_amount: item.sales_price || item.calculation.total_amount,
      module_enabled: true,
    });
  };

  const handleCalculationSelect = async (selectedCalculations) => {
    if (selectedCalculations && selectedCalculations.length > 0) {
      try {
        const calcData = await fetchOfferCalculation(selectedCalculations[0].id);
        if (calcData) {
          if (isModuleCalculation(calcData)) {
            setCalculationSelectorOpen(false);
            setPendingCalculationForInsert({
              ...calcData,
              module_enabled: true,
            });
          } else {
            insertCalculationInOffer(calcData);
          }
        }
      } catch (error) {
        console.error("Error fetching calculation:", error);
        toast.error("Kunne ikke laste kalkyle");
      }
    }
  };

  const handleEditCalculation = async (item, isNested = false) => {
    // If there's already a dialog open, push current state to stack
    if (editingCalculation && selectedItemForCalculation) {
      setCalculationDialogStack(prev => [...prev, {
        calculation: editingCalculation,
        selectedItem: selectedItemForCalculation
      }]);
    }
    
    setSelectedItemForCalculation({
      ...item,
      nested_calculation_id: isNested ? item.nested_calculation_id : undefined
    });
    
    // Open dialog immediately with existing calculation data (if available)
    // This makes the UI responsive while we fetch full data
    if (item.calculation) {
      setEditingCalculation({
        ...item.calculation,
        items: item.calculation.items || []
      });
    } else {
      // Set a placeholder if no calculation data exists
      setEditingCalculation({
        id: null,
        title: "Laster kalkyle...",
        items: []
      });
    }
    
    // Show loading indicator
    setLoadingCalculation(true);
    
    // Fetch full calculation data with items when opening dialog
    // The calculation in item.calculation might not have items loaded
    const calcId = isNested ? item.nested_calculation_id : item.calculation_id;
    if (calcId) {
      try {
        const calcData = await fetchOfferCalculation(calcId);
        console.log("Fetched calculation data:", {
          id: calcData?.id,
          items_count: calcData?.items?.length || 0,
          items: calcData?.items,
          isNested: isNested
        });
        
        // Ensure items array exists, even if empty
        if (calcData) {
          const calculationWithItems = {
            ...calcData,
            items: calcData.items || []
          };
          setEditingCalculation(calculationWithItems);
        } else {
          // Fallback to item.calculation if fetch fails
          setEditingCalculation(item.calculation || {
            id: null,
            title: "Kalkyle ikke funnet",
            items: []
          });
        }
      } catch (error) {
        console.error("Error fetching calculation:", error);
        // Fallback to item.calculation if fetch fails
        setEditingCalculation(item.calculation || {
          id: null,
          title: "Feil ved lasting av kalkyle",
          items: []
        });
      } finally {
        // Hide loading indicator
        setLoadingCalculation(false);
      }
    } else {
      setLoadingCalculation(false);
    }
  };

  const handleCloseCalculationDialog = () => {
    // Reset loading state when closing
    setLoadingCalculation(false);
    
    // If there's a dialog in the stack, restore it
    if (calculationDialogStack.length > 0) {
      const previousDialog = calculationDialogStack[calculationDialogStack.length - 1];
      setCalculationDialogStack(prev => prev.slice(0, -1));
      setEditingCalculation(previousDialog.calculation);
      setSelectedItemForCalculation(previousDialog.selectedItem);
    } else {
      // No more dialogs in stack, close completely
      setEditingCalculation(null);
      setSelectedItemForCalculation(null);
    }
  };

  const handleSaveCalculation = async (updatedCalculation, saveTemplate = false) => {
    if (selectedItemForCalculation) {
      try {
        // Check if this is a nested calculation update
        const isNestedUpdate = !!selectedItemForCalculation.nested_calculation_id;
        
        // Calculate total sales price from calculation items
        const totalSales = calculateCalculationTotalAmount(updatedCalculation);
        
        // Calculate total cost from calculation items
        const totalCost = calculateCalculationCost(updatedCalculation);
        
        // Update calculation object with new total_amount
        // Make sure items are included from updatedCalculation
        // Also ensure purchase_price is calculated for items with automatic price mode
        // And ensure product_id and unit_id are included from product/unit objects if not set
        const itemsWithPurchasePrice = (updatedCalculation.items || updatedCalculation[name] || []).map(item => {
          // Extract product_id from product object if product_id is not set
          let product_id = item.product_id;
          if (!product_id && item.product) {
            product_id = item.product.id || item.product;
          }
          
          // Extract unit_id from unit object if unit_id is not set
          let unit_id = item.unit_id;
          if (!unit_id && item.unit) {
            unit_id = item.unit.id || item.unit;
          }
          
          // For hours lines with automatic price mode, calculate purchase_price if not set
          if (item.line_type === 'hours' && item.price_mode === 'automatic' && (!item.purchase_price || item.purchase_price === 0)) {
            const hours = parseFloat(item.hours || 0);
            const costHourRate = parseFloat(item.cost_hour_rate || 0);
            const quantity = parseFloat(item.quantity || 1);
            let calculatedHours = hours;
            
            // Apply divide_by_quantity if needed
            if (item.divide_by_quantity && quantity > 0) {
              calculatedHours = calculatedHours / quantity;
            }
            
            // Calculate purchase_price: hours * cost_hour_rate * quantity
            if (calculatedHours > 0 && costHourRate > 0) {
              const calculatedPurchasePrice = calculatedHours * costHourRate * quantity;
              return {
                ...item,
                product_id: product_id,
                unit_id: unit_id,
                purchase_price: calculatedPurchasePrice
              };
            }
          }
          
          // Return item with product_id and unit_id included
          return {
            ...item,
            product_id: product_id,
            unit_id: unit_id
          };
        });
        
        const updatedCalcWithTotal = {
          ...updatedCalculation,
          total_amount: totalSales,
          items: itemsWithPurchasePrice, // Use items with calculated purchase_price
        };
        
        // Check if we have a valid calculation ID
        // For nested calculations, use nested_calculation_id
        // Otherwise use calculation_id from item first (this should be offer-specific if it exists)
        // Otherwise use the calculation's own ID
        let calculationId = isNestedUpdate 
          ? selectedItemForCalculation.nested_calculation_id 
          : (selectedItemForCalculation.calculation_id || updatedCalcWithTotal.id);
        
        // Get offerId from values (it should be available if offer is saved)
        // values comes from Formik and should contain the offer's id field
        const currentOfferId = values?.id || values?.offer_id;
        
        // Check if this is a template being edited in an offer context
        const isTemplate = updatedCalcWithTotal.offer_id === null || updatedCalcWithTotal.offer_id === undefined;
        const isBeingEditedInOffer = !!currentOfferId;
        
        // If this is a template being edited in an offer, ask user if they want to save template
        // BUT only if saveTemplate is not explicitly set to false (i.e., user hasn't already chosen)
        if (isTemplate && isBeingEditedInOffer && saveTemplate === undefined && calculationId) {
          // Check if this is the first time saving (no offer_id in calculation yet)
          // Store pending save data and show dialog
          setPendingCalculationSave({
            updatedCalcWithTotal,
            calculationId,
            currentOfferId,
            isNestedUpdate,
            selectedItemForCalculation,
            totalSales,
            totalCost
          });
          setSaveTemplateDialogOpen(true);
          return;
        }
        
        // Only proceed if we have an offer ID (calculation can be created/updated even without calculationId)
        if (currentOfferId) {
          // We have an offer ID, so we can create/update offer-specific calculation
          // Prepare calculation data for API - ensure items are included
          const calculationPayload = {
            ...updatedCalcWithTotal,
            offer_id: currentOfferId, // Include offer_id to make it offer-specific
            items: updatedCalcWithTotal.items || [], // Ensure items array is included
            save_template: saveTemplate, // Flag to indicate if template should be saved
          };
          
          console.log("Sending calculation payload:", {
            id: calculationPayload.id,
            offer_id: calculationPayload.offer_id,
            items_count: calculationPayload.items?.length || 0,
            total_amount: calculationPayload.total_amount,
            save_template: saveTemplate
          });
          
          // When saving "only for offer" (saveTemplate = false), we need to:
          // 1. Check if there's already an offer-specific calculation (has offer_id)
          // 2. If yes, update it (PUT)
          // 3. If no, create a new offer-specific version (POST)
          
          // Check if calculationId is valid
          const isValidId = calculationId && 
                           calculationId !== 'undefined' && 
                           calculationId !== 'null' && 
                           calculationId !== undefined && 
                           calculationId !== null &&
                           !isNaN(Number(calculationId));
          
          let result;
          
          // If saving only for offer (saveTemplate = false), always create/update offer-specific version
          if (saveTemplate === false) {
            // Check if we already have an offer-specific calculation
            // An offer-specific calculation has offer_id set, so check if the current calculation has it
            const isAlreadyOfferSpecific = updatedCalcWithTotal.offer_id === currentOfferId;
            
            if (isAlreadyOfferSpecific && isValidId) {
              // Update existing offer-specific calculation (PUT)
              calculationPayload.id = calculationId;
              console.log("Updating existing offer-specific calculation:", calculationPayload.id);
              result = await dispatch(updateOfferCalculationAsync(calculationPayload));
            } else {
              // Create new offer-specific calculation (POST) - copy from template
              // Include original_calculation_id to link to template
              if (calculationId && isValidId) {
                calculationPayload.original_calculation_id = calculationId;
              }
              // Don't include the template ID as the new calculation's ID
              delete calculationPayload.id;
              console.log("Creating new offer-specific calculation from template:", calculationPayload.original_calculation_id);
              result = await dispatch(createOfferCalculationAsync(calculationPayload));
            }
          } else if (isValidId) {
            // Update existing calculation (either template or offer-specific)
            calculationPayload.id = calculationId;
            result = await dispatch(updateOfferCalculationAsync(calculationPayload));
          } else {
            // Create new calculation
            // Include original_calculation_id if this is a copy of a template
            if (selectedItemForCalculation.original_calculation_id) {
              calculationPayload.original_calculation_id = selectedItemForCalculation.original_calculation_id;
            }
            result = await dispatch(createOfferCalculationAsync(calculationPayload));
          }
          
          if (result.type?.endsWith('/rejected')) {
            toast.error("Kunne ikke lagre kalkyle");
            return false; // Return false to indicate failure
          }
          
          // Update calculation with response data
          const savedCalculation = result.payload || updatedCalcWithTotal;
          
          // Update local state with saved calculation
          let prevItems = [...items];
          
          // Check if this is a nested calculation update
          if (isNestedUpdate) {
            // Find the item that contains this nested calculation
            const itemIndex = prevItems.findIndex(
              (item) => item.nested_calculation_id === savedCalculation.id || 
                       item.nested_calculation?.id === savedCalculation.id
            );
            
            if (itemIndex !== -1) {
              const parentItem = prevItems[itemIndex];
              // Re-fetch the nested calculation to ensure we have the latest data
              try {
                const refreshedNestedCalc = await fetchOfferCalculation(savedCalculation.id);
                if (refreshedNestedCalc) {
                  const updatedNestedCalc = {
                    ...refreshedNestedCalc,
                    divide_by_quantity: parentItem.nested_calculation?.divide_by_quantity || false,
                  };
                  prevItems[itemIndex] = {
                    ...parentItem,
                    nested_calculation: updatedNestedCalc,
                    nested_calculation_id: refreshedNestedCalc.id,
                    // Recalculate sales_price based on nested calculation and other factors
                    sales_price: (() => {
                      // Use calculateAutomaticSalesPrice logic if available, otherwise calculate manually
                      let nestedAmount = parseFloat(refreshedNestedCalc.total_amount || 0);
                      const qty = parseFloat(parentItem.quantity || 1);
                      // Apply divide_by_quantity logic if needed
                      if (updatedNestedCalc.divide_by_quantity && qty > 0) {
                        nestedAmount = nestedAmount / qty;
                      } else if (parentItem.divide_by_quantity && qty > 0) {
                        nestedAmount = nestedAmount / qty;
                      }
                      // Add products and hours if they exist
                      let total = nestedAmount;
                      // Add products totals if exists
                      if (parentItem.products && Array.isArray(parentItem.products)) {
                        parentItem.products.forEach((prod) => {
                          const prodQuantity = parseFloat(prod.quantity || 1);
                          let prodSalesPrice = parseFloat(prod.sales_price || 0);
                          const prodDiscount = parseFloat(prod.discount || 0);
                          const prodSurcharge = parseFloat(prod.surcharge || 0);
                          
                          if (prod.divide_by_quantity && qty > 0) {
                            prodSalesPrice = prodSalesPrice / qty;
                          }
                          
                          if (prodSalesPrice > 0 && prodQuantity > 0) {
                            let prodTotal = prodSalesPrice * prodQuantity;
                            if (prodSurcharge > 0) prodTotal += (prodTotal * prodSurcharge) / 100;
                            if (prodDiscount > 0) prodTotal -= (prodTotal * prodDiscount) / 100;
                            total += prodTotal;
                          }
                        });
                      }
                      // Add hours calculation if exists
                      let hours = parseFloat(parentItem.hours || 0);
                      const hourRate = parseFloat(parentItem.hour_rate || 0);
                      if (parentItem.divide_by_quantity && qty > 0) {
                        hours = hours / qty;
                      }
                      if (hours > 0 && hourRate > 0) {
                        total += hours * hourRate;
                      }
                      return total;
                    })(),
                  };
                } else {
                  // Fallback to saved calculation if refresh fails
                  const updatedNestedCalc = {
                    ...savedCalculation,
                    divide_by_quantity: parentItem.nested_calculation?.divide_by_quantity || false,
                  };
                  prevItems[itemIndex] = {
                    ...parentItem,
                    nested_calculation: updatedNestedCalc,
                    nested_calculation_id: savedCalculation.id,
                    // Recalculate sales_price based on nested calculation and other factors
                    sales_price: (() => {
                      let nestedAmount = parseFloat(savedCalculation.total_amount || 0);
                      const qty = parseFloat(parentItem.quantity || 1);
                      if (updatedNestedCalc.divide_by_quantity && qty > 0) {
                        nestedAmount = nestedAmount / qty;
                      } else if (parentItem.divide_by_quantity && qty > 0) {
                        nestedAmount = nestedAmount / qty;
                      }
                      let total = nestedAmount;
                      if (parentItem.products && Array.isArray(parentItem.products)) {
                        parentItem.products.forEach((prod) => {
                          const prodQuantity = parseFloat(prod.quantity || 1);
                          let prodSalesPrice = parseFloat(prod.sales_price || 0);
                          const prodDiscount = parseFloat(prod.discount || 0);
                          const prodSurcharge = parseFloat(prod.surcharge || 0);
                          
                          if (prod.divide_by_quantity && qty > 0) {
                            prodSalesPrice = prodSalesPrice / qty;
                          }
                          
                          if (prodSalesPrice > 0 && prodQuantity > 0) {
                            let prodTotal = prodSalesPrice * prodQuantity;
                            if (prodSurcharge > 0) prodTotal += (prodTotal * prodSurcharge) / 100;
                            if (prodDiscount > 0) prodTotal -= (prodTotal * prodDiscount) / 100;
                            total += prodTotal;
                          }
                        });
                      }
                      let hours = parseFloat(parentItem.hours || 0);
                      const hourRate = parseFloat(parentItem.hour_rate || 0);
                      if (parentItem.divide_by_quantity && qty > 0) {
                        hours = hours / qty;
                      }
                      if (hours > 0 && hourRate > 0) {
                        total += hours * hourRate;
                      }
                      return total;
                    })(),
                  };
                }
              } catch (error) {
                console.error("Error refreshing nested calculation:", error);
                // Fallback to saved calculation if refresh fails
                const updatedNestedCalc = {
                  ...savedCalculation,
                  divide_by_quantity: parentItem.nested_calculation?.divide_by_quantity || false,
                };
                prevItems[itemIndex] = {
                  ...parentItem,
                  nested_calculation: updatedNestedCalc,
                  nested_calculation_id: savedCalculation.id,
                  sales_price: parseFloat(savedCalculation.total_amount || 0),
                };
              }
            }
          } else {
            // Regular calculation update
            const itemIndex = prevItems.findIndex(
              (item) => item.row_order === selectedItemForCalculation.row_order
            );
            
            if (itemIndex !== -1) {
              // Re-fetch the calculation to ensure we have the latest data including nested calculations
              try {
                const refreshedCalc = await fetchOfferCalculation(savedCalculation.id);
                if (refreshedCalc) {
                  // Calculate the correct sales_price - use total_amount directly (not multiplied by quantity)
                  // The quantity is already accounted for in the calculation's total_amount
                  const calcTotalAmount = parseFloat(refreshedCalc.total_amount || 0);
                  // Use purchase_amount from calculation if available, otherwise use calculated totalCost
                  const calcPurchaseAmount = parseFloat(refreshedCalc.purchase_amount || totalCost || 0);
                  
                  prevItems[itemIndex] = {
                    ...prevItems[itemIndex],
                    calculation: refreshedCalc, // Use refreshed calculation from API
                    sales_price: calcTotalAmount, // Use total_amount directly
                    purchase_price: calcPurchaseAmount, // Use purchase_amount from calculation
                    item_type: prevItems[itemIndex].item_type || prevItems[itemIndex].type || "calculation",
                    type: prevItems[itemIndex].type || "calculation",
                    calculation_id: refreshedCalc.id, // Update with saved calculation ID (offer-specific if created)
                    // Keep original_calculation_id if this is a copy from template
                    original_calculation_id: refreshedCalc.original_calculation_id || prevItems[itemIndex].original_calculation_id || calculationId,
                  };
                } else {
                  // Fallback to saved calculation if refresh fails
                  prevItems[itemIndex] = {
                    ...prevItems[itemIndex],
                    calculation: savedCalculation, // Use full saved calculation from API
                    sales_price: parseFloat(savedCalculation.total_amount || totalSales),
                    purchase_price: totalCost,
                    item_type: prevItems[itemIndex].item_type || prevItems[itemIndex].type || "calculation",
                    type: prevItems[itemIndex].type || "calculation",
                    calculation_id: savedCalculation.id, // Update with saved calculation ID
                  };
                }
              } catch (error) {
                console.error("Error refreshing calculation:", error);
                // Fallback to saved calculation if refresh fails
                prevItems[itemIndex] = {
                  ...prevItems[itemIndex],
                  calculation: savedCalculation, // Use full saved calculation from API
                  sales_price: parseFloat(savedCalculation.total_amount || totalSales),
                  purchase_price: totalCost,
                  item_type: prevItems[itemIndex].item_type || prevItems[itemIndex].type || "calculation",
                  type: prevItems[itemIndex].type || "calculation",
                  calculation_id: savedCalculation.id, // Update with saved calculation ID
                };
              }
            }
          }
          
          isInternalUpdateRef.current = true;
          setItems(prevItems);
          
          // Update Formik form so changes are reflected in form state
          const mappedItems = prevItems.map(item => ({
            ...item,
            item_type: item.item_type || item.type,
          }));
          setFieldValue(name, mappedItems);
          
          // After saving, check if we should return to previous dialog or close
          if (calculationDialogStack.length > 0) {
            // Return to previous dialog in stack
            const previousDialog = calculationDialogStack[calculationDialogStack.length - 1];
            setCalculationDialogStack(prev => prev.slice(0, -1));
            // Re-fetch the previous calculation to get updated data
            const prevCalcId = previousDialog.selectedItem.calculation_id || previousDialog.selectedItem.nested_calculation_id;
            if (prevCalcId) {
              try {
                const refreshedCalc = await fetchOfferCalculation(prevCalcId);
                setEditingCalculation({
                  ...refreshedCalc,
                  items: refreshedCalc.items || []
                });
                setSelectedItemForCalculation(previousDialog.selectedItem);
              } catch (error) {
                console.error("Error refreshing previous calculation:", error);
                setEditingCalculation(previousDialog.calculation);
                setSelectedItemForCalculation(previousDialog.selectedItem);
              }
            } else {
              setEditingCalculation(previousDialog.calculation);
              setSelectedItemForCalculation(previousDialog.selectedItem);
            }
          } else {
            // No more dialogs, close completely
            setEditingCalculation(null);
            setSelectedItemForCalculation(null);
          }
          toast.success("Kalkyle lagret");
          return true; // Return true to indicate success
        } else {
          // No offer ID yet - calculation will be saved when offer is created
          // Update local state anyway
          let prevItems = [...items];
          const itemIndex = prevItems.findIndex(
            (item) => item.row_order === selectedItemForCalculation.row_order
          );
          
          if (itemIndex !== -1) {
            prevItems[itemIndex] = {
              ...prevItems[itemIndex],
              calculation: updatedCalcWithTotal,
              sales_price: updatedCalcWithTotal.total_amount || totalSales,
              purchase_price: totalCost,
              item_type: prevItems[itemIndex].item_type || prevItems[itemIndex].type || "calculation",
              type: prevItems[itemIndex].type || "calculation",
            };
            isInternalUpdateRef.current = true;
            setItems(prevItems);
            
            // Update Formik form so changes are reflected in form state
            const mappedItems = prevItems.map(item => ({
              ...item,
              item_type: item.item_type || item.type,
            }));
            setFieldValue(name, mappedItems);
          }
          
          toast.info("Kalkyle vil bli lagret når tilbudet lagres");
          return true; // Return true to indicate success
        }
      } catch (error) {
        console.error("Error saving calculation:", error);
        toast.error("Kunne ikke lagre kalkyle");
        return false; // Return false to indicate failure
      }
    }
    return false; // Return false if selectedItemForCalculation is not set
  };

  const onItemRowChange = (e, data) => {
    e.preventDefault();

    let prevItems = [...items];
    // Find item by id if available, otherwise by row_order
    let itemIndex = -1;
    if (data.id) {
      itemIndex = prevItems.findIndex(item => item.id === data.id);
    }
    // Fallback to row_order if id not found or not available
    if (itemIndex === -1 && data.row_order !== undefined) {
      itemIndex = prevItems.findIndex(item => item.row_order === data.row_order);
    }
    // Final fallback: use row_order as array index if still not found
    if (itemIndex === -1 && data.row_order !== undefined && data.row_order < prevItems.length) {
      itemIndex = data.row_order;
    }
    
    if (itemIndex === -1) {
      console.error("Could not find item to update:", data);
      return;
    }
    
    let item = {
      ...prevItems[itemIndex],
      [e.target.name]: e.target.value,
    };
    prevItems[itemIndex] = item;
    isInternalUpdateRef.current = true;
    setItems(prevItems);
  };

  const calculateRowTotalPrice = (item) => {
    const salesPriceDivided = item.sales_price / 100;
    const surcharge = item.surcharge ? salesPriceDivided * item.surcharge : 0;
    const discount = item.discount ? salesPriceDivided * item.discount : 0;
    var total = 0;
    if (item && item.sales_price && item.quantity) {
      total =
        (parseInt(item.sales_price) +
          parseInt(surcharge) -
          parseInt(discount)) *
        item.quantity;
    }

    return total;
  };

  const calculateTotalPrice = (items) => {
    var total = 0;
    if (items.length > 0) {
      items?.map((item) => {
        if (item.group_row == false) {
          total += calculateRowTotalPrice(item);
        }
      });
    }

    return total;
  };

  const calculateRowTotalPurchasePrice = (item) => {
    var total = 0.0;
    if (item && item.purchase_price && item.quantity) {
      total = item.purchase_price * item.quantity;
    }

    return total;
  };

  const calculateTotalPurchasePrice = (items) => {
    var total = 0;
    if (items?.length > 0) {
      items?.map((item) => {
        if (item.group_row == false) {
          total += calculateRowTotalPurchasePrice(item);
        }
      });
    }

    return total;
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    isInternalUpdateRef.current = true;
    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const handleRowProductSelect = (row) => {
    setSelectedItemRow(row);
    setProductSelectorOpen(true);
  };

  const handleProductSelect = (data) => {
    setProductSelectorOpen(false);
    if (selectedItemRow && data && data.length > 0) {
      let prevItems = [...items];
      let item = {
        ...prevItems[selectedItemRow.row_order],
        product: data[0],
        product_id: data[0].id,
        unit_id: data[0].unit?.id,
        unit: data[0].unit,
        sales_price: data[0].sales_price,
        purchase_price: data[0].purchase_price,
        quantity: "1.0",
      };
      prevItems[selectedItemRow.row_order] = item;
      isInternalUpdateRef.current = true;
      setItems(prevItems);
    }
  };

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);

  const handleDeleteRow = async () => {
    if (!selectedItemRow && !itemIdToDelete) return;
    
    setDeleteConfirmationOpen(false);
    const idToDelete = itemIdToDelete || selectedItemRow;
    
    // Item is already marked as deleting from deleteRow function
    // Now remove from local state immediately for better UX
    const newArray = items.filter((item) => {
      // Filter by id if available
      if (idToDelete && item.id) {
        return item.id !== idToDelete;
      }
      // For items without id (like groups), use draggableId or row_order
      const itemToDelete = items.find(i => i.id === idToDelete || i.draggableId === idToDelete || (typeof idToDelete === 'string' && idToDelete.startsWith('temp_') && i.row_order === parseInt(idToDelete.replace('temp_', ''))));
      if (itemToDelete && !item.id) {
        // Both are new items, compare by draggableId or row_order
        return item.draggableId !== itemToDelete.draggableId && item.row_order !== itemToDelete.row_order;
      }
      return true;
    });
    isInternalUpdateRef.current = true;
    setItems(orderItems(newArray));
    
    // Update Formik immediately
    if (setFieldValue) {
      const mappedItems = newArray.map(item => ({
        ...item,
        item_type: item.item_type || item.type,
      }));
      setFieldValue(name, mappedItems);
    }
    
    // Clear deleting state immediately
    setDeletingItems(prev => {
      const newSet = new Set(prev);
      if (idToDelete) {
        newSet.delete(idToDelete);
      }
      return newSet;
    });
    setSelectedItemRow(null);
    setItemIdToDelete(null);
  };

  const deleteRow = (id) => {
    // For groups and other items without id, use row_order or draggableId
    if (!id) {
      // Find item by other means if id is not available
      const itemToDelete = items.find(item => 
        !item.id && (item.row_order !== undefined || item.draggableId)
      );
      if (itemToDelete) {
        // Use a temporary identifier
        const tempId = itemToDelete.draggableId || `temp_${itemToDelete.row_order}`;
        setItemIdToDelete(tempId);
        setDeletingItems(prev => new Set(prev).add(tempId));
        setSelectedItemRow(itemToDelete);
        setDeleteConfirmationOpen(true);
        return;
      }
    }
    // Mark as deleting immediately to prevent multiple clicks
    setDeletingItems(prev => new Set(prev).add(id));
    setSelectedItemRow(id);
    setItemIdToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const groupRow = (item, provided, snapshot) => {
    const colSpan = 2 + (drag ? 1 : 0) + (props.quantity ? 1 : 0) + (props.purchase_price ? 1 : 0) + (props.sales_price ? (3 + (props.discount ? 1 : 0) + (props.surcharge ? 1 : 0)) : 0);
    
    return (
      <div
        className="bg-gray-100 border border-gray-300 rounded-lg shadow-sm"
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <Table>
          <TableBody>
            <TableRow>
              {drag && (
                <TableCell style={{ width: 48, padding: '8px' }}>
                  <DragHandleIcon className="text-gray-500" />
                </TableCell>
              )}
              <TableCell align="left" colSpan={colSpan}>
                <div className="flex items-center gap-4">
                  <TextField
                    label="Gruppetittel"
                    variant="standard"
                    size="small"
                    name="title"
                    sx={{ minWidth: 200 }}
                    value={item.title || ""}
                    onChange={(e) => onItemRowChange(e, item)}
                  />
                  <TextField
                    label="Beskrivelse"
                    variant="standard"
                    size="small"
                    name="description"
                    sx={{ flex: 1, maxWidth: 400 }}
                    multiline
                    rows={1}
                    value={item.description || ""}
                    onChange={(e) => onItemRowChange(e, item)}
                  />
                </div>
              </TableCell>
              <TableCell align="right" style={{ width: 60 }}>
                <div className="flex items-center justify-end gap-2">
                  {((item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) || 
                    (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)))) && (
                    <span className="text-xs text-gray-500">Laster...</span>
                  )}
                  <TrashIcon
                    className={`h-5 w-5 ${
                      (item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                      (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)))
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-red-400 cursor-pointer hover:text-red-600"
                    }`}
                    onClick={() => {
                      const isDeleting = (item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                                        (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)));
                      if (!isDeleting) {
                        deleteRow(item.id || item.draggableId || `temp_${item.row_order}`);
                      }
                    }}
                    style={{
                      pointerEvents: ((item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                                     (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)))) ? "none" : "auto"
                    }}
                  />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };

  const calculationRow = (item, provided, snapshot) => {
    return (
      <div
        className="bg-white border border-blue-200 rounded-lg shadow-sm hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {drag && (
                <DragHandleIcon className="text-gray-400" />
              )}
              <CalculatorIcon className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-semibold text-blue-700">Kalkyle</span>
              {item.calculation?.is_copy && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  Kopi
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {item.calculation && isModuleCalculation(item.calculation) ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CalculatorIcon className="h-4 w-4" />}
                  onClick={() => handleAdjustCalculationFields(item)}
                >
                  {t("offer.calculation.insert.adjust_fields")}
                </Button>
              ) : null}
              {item.calculation && !isModuleCalculation(item.calculation) ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PencilSquareIcon className="h-4 w-4" />}
                  onClick={() => handleEditCalculation(item)}
                >
                  {t("offer.calculation.insert.edit_lines")}
                </Button>
              ) : null}
              <TrashIcon
                className={`h-5 w-5 ${
                  (item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                  (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)))
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-red-400 cursor-pointer hover:text-red-600"
                }`}
                onClick={() => {
                  const isDeleting = (item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                                    (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)));
                  if (!isDeleting) {
                    deleteRow(item.id || item.draggableId || `temp_${item.row_order}`);
                  }
                }}
                style={{
                  pointerEvents: ((item.id && (deletingItems.has(item.id) || savingItems.has(item.id))) ||
                                 (!item.id && (deletingItems.has(item.draggableId) || deletingItems.has(`temp_${item.row_order}`)))) ? "none" : "auto"
                }}
              />
            </div>
          </div>
        </div>
        <div className="px-4 py-4">

          {item.calculation ? (
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-900">
                  {item.calculation.title || `Kalkyle #${item.calculation.id}`}
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div>
                    <div className="text-xs text-gray-500">Total kostpris</div>
                    <div className="text-sm font-semibold text-gray-700">
                      {formatter.format(item.purchase_price || item.calculation.purchase_amount || calculateCalculationCost(item.calculation) || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Total salgspris</div>
                    <div className="text-sm font-semibold text-gray-700">
                      {formatter.format(item.calculation.total_amount || 0)}
                    </div>
                  </div>
                </div>
              </div>
              {/* Show total hours if calculation has them */}
              {(() => {
                const totalHours = calculateCalculationTotalHours(item.calculation);
                return (
                  totalHours > 0 && (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Total timer beregnet:</span> {totalHours.toFixed(2)}t
                    </div>
                  )
                );
              })()}
              {isModuleCalculation(item.calculation) ? (
                <div className="mt-3 rounded-lg border border-teal-100 bg-teal-50 px-3 py-2 text-xs text-teal-800">
                  <div className="font-semibold">{t("offer.calculation.insert.adjustable_fields")}</div>
                  <div>{t("offer.calculation.insert.adjustable_fields_hint")}</div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mb-4">
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalculatorIcon className="h-4 w-4" />}
                onClick={() => {
                  setSelectedItemForCalculation(item);
                  setCalculationSelectorOpen(true);
                }}
              >
                Velg kalkyle
              </Button>
            </div>
          )}

          {/* Standard fields for calculation row */}
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Antall"
                variant="standard"
                type="number"
                name="quantity"
                value={item?.quantity || 1}
                onChange={(e) => onItemRowChange(e, item)}
                fullWidth
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Salgspris"
                variant="standard"
                type="number"
                name="sales_price"
                value={item?.sales_price || (item.calculation?.total_amount || 0)}
                onChange={(e) => onItemRowChange(e, item)}
                fullWidth
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label="Rabatt %"
                variant="standard"
                type="number"
                name="discount"
                value={item?.discount || 0}
                onChange={(e) => onItemRowChange(e, item)}
                fullWidth
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <div className="text-right">
                <div className="text-sm text-gray-500">Total:</div>
                <div className="font-bold text-lg">
                  {formatter.format(calculateRowTotalPrice(item))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const itemRow = (item, provided, snapshot) => {
    return (
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <Table>
          <TableBody>
            <TableRow>
              {drag && (
                <TableCell 
                  style={{ width: 48, padding: '8px' }}
                  className="align-middle"
                >
                  <DragHandleIcon className="text-gray-400" />
                </TableCell>
              )}
              <TableCell align="left" style={{ minWidth: 200 }}>
                <div
                  className="cursor-pointer mb-2"
                  onClick={() => handleRowProductSelect(item)}
                >
                  {item?.product?.id ? (
                    <div className="font-semibold text-gray-900">
                      {item?.product?.displayTitle}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">Velg produkt</div>
                  )}
                </div>
                <TextField
                  label="Beskrivelse"
                  variant="standard"
                  size="small"
                  value={item?.description || ""}
                  fullWidth
                  name="description"
                  multiline
                  rows={2}
                  onChange={(e) => onItemRowChange(e, item)}
                />
              </TableCell>

              {props.quantity && (
                <TableCell align="center" style={{ minWidth: 100 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Antall"
                    value={item?.quantity || ""}
                    name="quantity"
                    type="number"
                    onChange={(e) => onItemRowChange(e, item)}
                    sx={{ width: 80 }}
                  />
                </TableCell>
              )}

              {props.purchase_price && (
                <TableCell align="right" style={{ minWidth: 120 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Innkjøpspris"
                    value={item?.purchase_price || ""}
                    name="purchase_price"
                    type="number"
                    onChange={(e) => onItemRowChange(e, item)}
                    sx={{ width: 100 }}
                  />
                </TableCell>
              )}

              {props.sales_price && (
                <>
                  <TableCell align="right" style={{ minWidth: 120 }}>
                    <TextField
                      variant="standard"
                      size="small"
                      label="Salgspris"
                      value={item?.sales_price || ""}
                      name="sales_price"
                      type="number"
                      onChange={(e) => onItemRowChange(e, item)}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  {props.discount && (
                    <TableCell align="center" style={{ minWidth: 100 }}>
                      <TextField
                        variant="standard"
                        size="small"
                        label="Rabatt %"
                        value={item?.discount || 0}
                        name="discount"
                        type="number"
                        onChange={(e) => onItemRowChange(e, item)}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                  )}
                  {props.surcharge && (
                    <TableCell align="center" style={{ minWidth: 100 }}>
                      <TextField
                        variant="standard"
                        size="small"
                        label="Påslag %"
                        value={item?.surcharge || 0}
                        name="surcharge"
                        type="number"
                        onChange={(e) => onItemRowChange(e, item)}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="right" style={{ minWidth: 120 }}>
                    <div className="font-semibold text-gray-900">
                      {formatter.format(calculateRowTotalPrice(item))}
                    </div>
                  </TableCell>
                </>
              )}

              <TableCell align="right" style={{ width: 60 }}>
                <div className="flex items-center justify-end gap-2">
                  {(deletingItems.has(item.id) || savingItems.has(item.id)) && (
                    <span className="text-xs text-gray-500">Laster...</span>
                  )}
                  {item?.id && (
                    <TrashIcon
                      className={`h-5 w-5 ${
                        deletingItems.has(item.id) || savingItems.has(item.id)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-red-400 cursor-pointer hover:text-red-600"
                      }`}
                      onClick={() => {
                        if (!deletingItems.has(item.id) && !savingItems.has(item.id)) {
                          deleteRow(item.id);
                        }
                      }}
                      style={{
                        pointerEvents: deletingItems.has(item.id) || savingItems.has(item.id) ? "none" : "auto"
                      }}
                    />
                  )}
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };

  const textRow = (item, provided, snapshot) => {
    const updateTextRowDescription = (value) => {
      const itemIndex = items.findIndex(i => 
        (i.id && item.id && i.id === item.id) || 
        (i.row_order !== undefined && item.row_order !== undefined && i.row_order === item.row_order) ||
        (i.draggableId && item.draggableId && i.draggableId === item.draggableId)
      );
      if (itemIndex !== -1) {
        const updatedItems = [...items];
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          description: value
        };
        isInternalUpdateRef.current = true;
        setItems(updatedItems);
        if (setFieldValue) {
          const mappedItems = updatedItems.map(item => ({
            ...item,
            item_type: item.item_type || item.type,
          }));
          setFieldValue(name, mappedItems);
        }
      }
    };

    return (
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center gap-2">
            {drag && (
              <DragHandleIcon className="text-gray-400" />
            )}
            <span className="text-sm font-medium text-gray-700">Textrad</span>
          </div>
          <div className="flex items-center gap-2">
            {item?.id && (
              <TrashIcon
                className="h-5 w-5 text-red-400 cursor-pointer hover:text-red-600"
                onClick={() => deleteRow(item.id)}
              />
            )}
          </div>
        </div>
        <div className="px-4 py-4">
          <PromangRichTextEditor
            value={item?.description || ""}
            onChange={updateTextRowDescription}
            placeholder="Skriv tekst..."
            size="sm"
            templateButtonLabel="Mal"
          />
        </div>
      </div>
    );
  };

  function rowType(type, item, provided, snapshot) {
    switch (type) {
      case "item":
        return itemRow(item, provided, snapshot);
      case "group":
        return groupRow(item, provided, snapshot);
      case "text":
        return textRow(item, provided, snapshot);
      case "calculation":
        return calculationRow(item, provided, snapshot);
      default:
        return itemRow(item, provided, snapshot);
    }
  }

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      {/* Table Header */}
      {items && items.length > 0 && (
        <div className="mb-4">
          <Table>
            <TableHead>
              <TableRow className="bg-gray-50">
                {drag && <TableCell style={{ width: 48 }}></TableCell>}
                <TableCell align="left" style={{ minWidth: 200 }}>
                  Produkt/Beskrivelse
                </TableCell>
                {props.quantity && (
                  <TableCell align="center" style={{ minWidth: 100 }}>
                    Antall
                  </TableCell>
                )}
                {props.purchase_price && (
                  <TableCell align="right" style={{ minWidth: 120 }}>
                    Innkjøpspris
                  </TableCell>
                )}
                {props.sales_price && (
                  <>
                    <TableCell align="right" style={{ minWidth: 120 }}>
                      Salgspris
                    </TableCell>
                    {props.discount && (
                      <TableCell align="center" style={{ minWidth: 100 }}>
                        Rabatt %
                      </TableCell>
                    )}
                    {props.surcharge && (
                      <TableCell align="center" style={{ minWidth: 100 }}>
                        Påslag %
                      </TableCell>
                    )}
                    <TableCell align="right" style={{ minWidth: 120 }}>
                      Total
                    </TableCell>
                  </>
                )}
                <TableCell align="right" style={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-3">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items && items.length > 0 ? (
                  items.map((item, index) => (
                    <Draggable
                      key={item.id || item.draggableId}
                      draggableId={item.draggableId || `item-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) =>
                        rowType(item.item_type || item.type, item, provided, snapshot)
                      }
                    </Draggable>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-base font-medium">Ingen linjer lagt til ennå.</p>
                    <p className="text-sm mt-2 text-gray-400">
                      Klikk på &quot;Ny rad&quot;, &quot;Ny kalkyle&quot; eller &quot;Ny textrad&quot; for å legge til en linje.
                    </p>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Footer with Actions and Totals */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => addItemRow("item")}
              variant="outlined"
              size="small"
            >
              Ny rad
            </Button>
            <Button 
              onClick={() => addItemRow("text")}
              variant="outlined"
              size="small"
            >
              Ny textrad
            </Button>
            <Button 
              onClick={handleAddCalculationRow}
              startIcon={<CalculatorIcon className="h-5 w-5" />}
              variant="outlined"
              size="small"
            >
              Ny kalkyle
            </Button>
            {props.group && (
              <Button 
                onClick={() => addItemRow("group")}
                variant="outlined"
                size="small"
              >
                Ny gruppering
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
            {rows && props.purchase_price && (
              <div className="text-right">
                <div className="text-sm text-gray-600">Total innkjøpspris:</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatter.format(calculateTotalPurchasePrice(items))}
                </div>
              </div>
            )}
            {rows && props.sales_price && (
              <div className="text-right">
                <div className="text-sm text-gray-600">Total salgspris:</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatter.format(calculateTotalPrice(items))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ItemSelectorV2
        multiple={false}
        open={productSelectorOpen}
        onClose={() => setProductSelectorOpen(false)}
        selectedItems={selectedItemRow?.product ? [selectedItemRow.product] : []}
        onChange={(data) => handleProductSelect(data)}
      />

      {/* Calculation Selector Dialog */}
      <Dialog
        open={calculationSelectorOpen}
        onClose={() => setCalculationSelectorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Velg kalkyle</DialogTitle>
        <DialogContent>
          <ModernCalculationSelector
            open={calculationSelectorOpen}
            selectedCalculations={[]}
            onChange={handleCalculationSelect}
            onClose={() => setCalculationSelectorOpen(false)}
            multiple={false}
            showHeader={false}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(pendingCalculationForInsert)}
        onClose={() => {
          setPendingCalculationForInsert(null);
          setSelectedItemForCalculation(null);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{ style: { minHeight: "78vh" } }}
      >
        <DialogTitle>
          {t("offer.calculation.insert.offer_dialog_title")}
        </DialogTitle>
        {pendingCalculationForInsert ? (
          <Formik
            enableReinitialize
            initialValues={{
              ...pendingCalculationForInsert,
              module_graph: pendingCalculationForInsert.module_graph || null,
              module_result: pendingCalculationForInsert.module_result || null,
            }}
            onSubmit={(calculationValues) => insertCalculationInOffer(calculationValues)}
          >
            {(formik) => (
              <>
                <DialogContent dividers>
                  <OfferCalculationUserInsertView {...formik} />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setPendingCalculationForInsert(null);
                      setSelectedItemForCalculation(null);
                    }}
                  >
                    {t("offer.calculation.insert.offer_cancel")}
                  </Button>
                  <Button
                    onClick={() => formik.submitForm()}
                    startIcon={<CalculatorIcon className="h-5 w-5" />}
                    variant="contained"
                  >
                    {t("offer.calculation.insert.offer_add")}
                  </Button>
                </DialogActions>
              </>
            )}
          </Formik>
        ) : null}
      </Dialog>

      {/* Edit Calculation Dialog */}
      {editingCalculation && selectedItemForCalculation && (
        <Dialog
          open={!!editingCalculation}
          onClose={handleCloseCalculationDialog}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              height: "90vh",
              maxHeight: "900px",
            },
          }}
        >
          <DialogTitle>
            Rediger kalkyle: {editingCalculation.title}
          </DialogTitle>
          <DialogContent>
            {loadingCalculation && (
              <Backdrop
                open={loadingCalculation}
                sx={{
                  position: "absolute",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                  <Box sx={{ color: "text.primary", fontWeight: 500 }}>
                    Åpner kalkyle...
                  </Box>
                </Box>
              </Backdrop>
            )}
            <Formik
              key={editingCalculation?.id || 'new'}
              initialValues={{
                ...editingCalculation,
                items: editingCalculation?.items || []
              }}
              enableReinitialize={true}
              onSubmit={handleSaveCalculation}
            >
              {(editFormik) => {
                console.log("Formik values in dialog:", {
                  items_count: editFormik.values.items?.length || 0,
                  items: editFormik.values.items,
                  calculation_id: editFormik.values.id
                });
                // Check if this is an offer-specific calculation (has offer_id)
                const isOfferSpecific = !!offerId;
                return (
                  <form>
                    {/* Title and Description fields - only show for offer-specific calculations */}
                    {isOfferSpecific && (
                      <div className="mb-4 space-y-4 pb-4 border-b border-gray-200">
                        <TextField
                          label="Tittel"
                          variant="standard"
                          fullWidth
                          name="title"
                          value={editFormik.values.title || ""}
                          onChange={editFormik.handleChange}
                          onBlur={editFormik.handleBlur}
                        />
                        <div>
                          <QuillEditor
                            name="description"
                            values={editFormik.values}
                            setFieldValue={editFormik.setFieldValue}
                            label="Beskrivelse"
                          />
                        </div>
                      </div>
                    )}
                    <OfferCalculationItems
                      name="items"
                      values={editFormik.values}
                      setFieldValue={editFormik.setFieldValue}
                      isLoading={false}
                      calculationType={editFormik.values.calculation_type}
                      calculationId={editingCalculation.id || editingCalculation.original_calculation_id}
                      onEditNestedCalculation={async (nestedCalc) => {
                        // Find the parent item that contains this nested calculation
                        const parentItem = items.find(item => 
                          item.nested_calculation_id === nestedCalc.id || 
                          item.nested_calculation?.id === nestedCalc.id
                        );
                        
                        if (parentItem) {
                          // Fetch full calculation data and open edit dialog
                          await handleEditCalculation({
                            ...parentItem,
                            nested_calculation_id: nestedCalc.id
                          }, true);
                        } else {
                          // Fallback: just fetch and open
                          try {
                            setLoadingCalculation(true);
                            try {
                              const calcData = await fetchOfferCalculation(nestedCalc.id);
                              if (calcData) {
                                setEditingCalculation({
                                  ...calcData,
                                  items: calcData.items || []
                                });
                                setSelectedItemForCalculation({
                                  row_order: -1, // Will be found by nested_calculation_id
                                  nested_calculation_id: nestedCalc.id
                                });
                              }
                            } finally {
                              setLoadingCalculation(false);
                            }
                          } catch (error) {
                            console.error("Error fetching nested calculation:", error);
                            toast.error("Kunne ikke laste kalkyle");
                          }
                        }
                      }}
                    />
                    <DialogActions className="mt-4">
                      <Button onClick={handleCloseCalculationDialog}>
                        Avbryt
                      </Button>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          // Ensure formik values are up to date before saving
                          const formValues = editFormik.values;
                          console.log("Saving calculation with formik values:", {
                            id: formValues.id,
                            items_count: formValues.items?.length || 0,
                            items: formValues.items
                          });
                          await handleSaveCalculation(formValues, false);
                        }}
                      >
                        Lagre
                      </Button>
                    </DialogActions>
                  </form>
                );
              }}
            </Formik>
          </DialogContent>
        </Dialog>
      )}

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        // item_id={confirmation.item_id}
        yesCallback={() => handleDeleteRow()}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          if (selectedItemRow || itemIdToDelete) {
            const idToClear = itemIdToDelete || selectedItemRow;
            setDeletingItems(prev => {
              const newSet = new Set(prev);
              newSet.delete(idToClear);
              return newSet;
            });
          }
          setSelectedItemRow(null);
          setItemIdToDelete(null);
        }}
      />

      {/* Dialog for choosing whether to save template or just offer version */}
      <Dialog
        open={saveTemplateDialogOpen}
        onClose={() => {
          setSaveTemplateDialogOpen(false);
          setPendingCalculationSave(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lagre kalkyle mal?</DialogTitle>
        <DialogContent>
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Du redigerer en kalkyle mal fra et tilbud. Vil du også oppdatere malen, eller bare lagre endringene for dette tilbudet?
            </p>
            <div className="space-y-2">
              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-900">Oppdater mal</p>
                <p className="text-xs text-blue-700 mt-1">
                  Endringene vil gjelde for alle fremtidige bruk av denne malen.
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-medium text-gray-900">Kun for dette tilbudet</p>
                <p className="text-xs text-gray-700 mt-1">
                  Endringene lagres kun for dette tilbudet. Malen forblir uendret.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSaveTemplateDialogOpen(false);
              setPendingCalculationSave(null);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (pendingCalculationSave) {
                try {
                  const success = await handleSaveCalculation(pendingCalculationSave.updatedCalcWithTotal, true);
                  // Only close dialog after successful save
                  if (success !== false) {
                    setSaveTemplateDialogOpen(false);
                    setPendingCalculationSave(null);
                  }
                } catch (error) {
                  console.error("Error saving calculation:", error);
                  toast.error("Kunne ikke lagre kalkyle");
                  // Don't close dialog on error
                }
              } else {
                setSaveTemplateDialogOpen(false);
                setPendingCalculationSave(null);
              }
            }}
          >
            Oppdater mal
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              if (pendingCalculationSave) {
                try {
                  const success = await handleSaveCalculation(pendingCalculationSave.updatedCalcWithTotal, false);
                  // Only close dialog after successful save
                  if (success !== false) {
                    setSaveTemplateDialogOpen(false);
                    setPendingCalculationSave(null);
                  }
                } catch (error) {
                  console.error("Error saving calculation:", error);
                  toast.error("Kunne ikke lagre kalkyle");
                  // Don't close dialog on error
                }
              } else {
                setSaveTemplateDialogOpen(false);
                setPendingCalculationSave(null);
              }
            }}
          >
            Kun for tilbudet
          </Button>
          </DialogActions>
        </Dialog>
    </React.Fragment>
  );
}

OfferItems.defaultProps = {
  group: false,
  purchase_price: false,
  sales_price: false,
  name: "items",
  drag: true,
  quantity_picked: false,
  quantity: true,
};
